import ClientPortalAPIService from './../services/api/ClientPortal/ApiService'
import MapBox from './map'
import { MyFleetPopupComponent } from '../components/MyFleetPopup/MyFleetPopup'
import Airport from './../modules/airport'
import * as turf from '@turf/turf'
import moment from 'moment'
import 'moment-timezone'
import { getStandarizedCoordinates } from '../services/MapService'
import { SearchBar } from '../components/SearchBar/SearchBar'

export class TailsModule {
    showTails = window.sessionStorage.getItem('showMapTails') === 'true' ? true : false
    trackedTailsByUser = []
    tails = null
    moduleInitialized = false
    isApiCallInProgress = false
    initialize() {
        this.getUserFlightTrackingInfo(true)
        this.startTailsObserver()
    }

    // This function is used externally to let know that some changes were made to the tails
    update() {
        if (!this.moduleInitialized) {
            this.initialize()
        } else {
            if (!this.isApiCallInProgress) {
                this.getUserFlightTrackingInfo(false)
            }
        }
    }

    getUserFlightTrackingInfo = (isFirstTime) => {
        this.isApiCallInProgress = true 
        ClientPortalAPIService.getUserFlightTrackingInfo().then(userFlightTrackingInfo => {
            userFlightTrackingInfo.forEach(flight => {
                // Calculate distance between the two airports
                let directDistanceKilometers = 0
                let directDistanceMiles = 0
                if (flight.destination !== undefined && flight.destination !== null) {
                    const airportOfOriginDetails = Airport.getAirportByICAOCode(flight.origin)
                    const airportOfDestinationDetails = Airport.getAirportByICAOCode(flight.destination)

                    flight.originIata = airportOfOriginDetails?.IATA
                    flight.destinationIata = airportOfDestinationDetails?.IATA

                    if (airportOfOriginDetails !== undefined && airportOfDestinationDetails !== undefined) {
                        const from = turf.point([airportOfOriginDetails.Lng, airportOfOriginDetails.Lat])
                        const to = turf.point([airportOfDestinationDetails.Lng, airportOfDestinationDetails.Lat])

                        directDistanceKilometers = Math.round(turf.distance(from, to, { units: 'kilometers' }))
                        directDistanceMiles = Math.round(turf.distance(from, to, { units: 'miles' }))
                    }
                }
                flight.directDistanceInKilometers = directDistanceKilometers
                flight.directDistanceInMiles = directDistanceMiles

                if (flight?.waypoints?.length > 0) {
                    flight.waypoints = flight.waypoints.map(item => [item.lon, item.lat])
                }
            })
            this.tails = userFlightTrackingInfo || []
            isFirstTime ? this.display() : this.updateDisplay()
        }).catch(error => {
            console.error('Error getting user flight tracking info', error)
        }).finally(() => {
            if (!this.moduleInitialized) {
                this.addEventsListeners()
                this.moduleInitialized = true
            }
            this.isApiCallInProgress = false 
        })
    }

    async getModuleTails() {
        const tails = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.tails !== null && this.moduleInitialized) {
                    resolve(this.tails)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return tails
    }

    startTailsObserver() {
        const SECONDS_30 = 1000 * 30
        window.setInterval(() => {
                if (!this.isApiCallInProgress) {
                    this.getUserFlightTrackingInfo(false)
                }
        }, SECONDS_30)
    }

    getTailsFeatureCollection(tails) {
        const featureCollection = {
            type: 'FeatureCollection',
            features: []
        }

        const guessedTimeZone = moment.tz(moment.tz.guess()).format('z')

        for (const tail of tails) {
            // We want to display only aircrafts that are in the air
            if (tail.status === 'en-route' && !tail.isGroundTail) {
                let aircraftName = ''
                let landingTime = ''
                let scheduledTime = ''
                const position = tail?.data?.position || {}
                const flifo = tail?.data?.flifo || {}

                tail.tailInfo.aircraftManufacturer + ' ' + tail.tailInfo.aircraftType
                if (tail.tailInfo.aircraftDescription !== '') {
                    aircraftName = aircraftName + ' (' + tail.tailInfo.aircraftDescription + ')'
                }

                if (flifo.estimatedOn) {
                    landingTime = moment.unix(flifo.estimatedOn).format('HH:mm') + ' ' + guessedTimeZone
                }

                if (flifo.scheduledOn) {
                    scheduledTime = moment.unix(flifo.scheduledOn).format('HH:mm') + ' ' + guessedTimeZone
                }

                const properties = {
                    ['latitude']: position?.lat,
                    ['longitude']: position?.lon,
                    ['origin']: tail.origin,
                    ['destination']: tail.destination,
                    ['originIATA']: tail.originIata,
                    ['destinationIATA']: tail.destinationIata,
                    ['heading']: position?.heading ? parseInt(position?.heading) : 0,
                    ['waypoints']: tail.waypoints,
                    ['identifier']: tail.tailNumber,
                    ['aircraftName']: aircraftName,
                    ['landingTime']: landingTime,
                    ['scheduledTime']: scheduledTime,
                    // Most recent groundspeed (knots)
                    ['groundSpeedKilometers']: position?.gs ? Math.round(position?.gs * 1.85200) : 0,
                    ['groundSpeedMiles']: position?.gs ? Math.round(position?.gs * 1.15078) : 0,
                    // Aircraft altitude in hundreds of feet
                    ['altitudeMeters']: position?.alt ? Math.round(position?.alt / 3.2808) : 0,
                    ['altitudeFeet']: position?.alt ? Math.round(position?.alt) : 0,
                    ['directDistanceInKilometers']: tail.directDistanceInKilometers,
                    ['directDistanceInMiles']: tail.directDistanceInMiles
                }

                const point = {
                    type: 'Point',
                    coordinates: [position?.lon, position?.lat]
                }

                const feature = {
                    type: 'Feature',
                    id: tail.faFlightId,
                    geometry: point,
                    properties: properties
                }

                featureCollection.features.push(feature)
            }
        }

        return featureCollection
    }

    getTailsRoutesFutureMultiLineString(tails) {
        const feature = {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'MultiLineString',
                coordinates: []
            }
        }

        for (const tail of tails) {
            // We want to display only aircrafts that are in the air
            if (tail.status === 'en-route') {
                if (tail.waypoints?.length > 0) {
                    const waypointCoordinates = tail.waypoints
                    const arrayOfCoordinates = getStandarizedCoordinates(waypointCoordinates)
                    feature.geometry.coordinates.push(arrayOfCoordinates)
                }
            }
        }
        return feature
    }

    display() {
        const tailsFeatureCollection = this.getTailsFeatureCollection(this.tails)
        const tailsRoutesFeatureMultiLineString = this.getTailsRoutesFutureMultiLineString(this.tails)

        // Check Avsec module for details about this timeout
        MapBox.map.addSource('tails-waypoints', {
            'type': 'geojson',
            'lineMetrics': true,
            'data': tailsRoutesFeatureMultiLineString
        })

        MapBox.map.addLayer({
            'id': 'tails-waypoints',
            'type': 'line',
            'source': 'tails-waypoints',
            'layout': {
                'visibility': this.showTails ? 'visible' : 'none',
                'line-join': 'round',
                'line-cap': 'round'
            },
            'paint': {
                'line-color': '#0085FF',
                'line-width': 2,
                'line-gradient': [
                    'interpolate',
                    ['linear'],
                    ['line-progress'],
                    0, 'rgba(0, 133, 255, 0.2)',
                    0.2, 'rgba(0, 133, 255, 1)',
                    0.55, 'rgba(0, 133, 255, 0.2)',
                    0.7, 'rgba(0, 133, 255, 1)',
                    1, 'rgba(0, 133, 255, 0.2)'
                ]
            }
        })

        MapBox.map.addSource('tails', {
            'type': 'geojson',
            'data': tailsFeatureCollection
        })

        MapBox.map.addLayer({
            id: 'tails',
            type: 'symbol',
            source: 'tails',
            layout: {
                'visibility': this.showTails ? 'visible' : 'none',
                'icon-image': 'tail-marker',
                'icon-rotate': ['get', 'heading'],
                'icon-allow-overlap': true,
                'text-allow-overlap': true,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 3.5, 0.2, 4.5, 0.3, 7, 0.4]
            }
        })
    }

    updateDisplay() {
        const tailsFeatureCollection = this.getTailsFeatureCollection(this.tails)
        const tailsRoutesFeatureMultiLineString = this.getTailsRoutesFutureMultiLineString(this.tails)
        const tailsSource = MapBox.map.getSource('tails')
        const tailsWaypointsSource = MapBox.map.getSource('tails-waypoints')

        if (!tailsSource && !tailsWaypointsSource) {
            this.display()
        } else {
            if (tailsSource) {
                tailsSource.setData(tailsFeatureCollection)
            }
            if (tailsWaypointsSource) {
                tailsWaypointsSource.setData(tailsRoutesFeatureMultiLineString)
            }
        }
    }

    show() {
        if (MapBox.map.getSource('tails') !== undefined) {
            MapBox.map.setLayoutProperty('tails', 'visibility', 'visible')
        }

        if (MapBox.map.getSource('tails-waypoints') !== undefined) {
            MapBox.map.setLayoutProperty('tails-waypoints', 'visibility', 'visible')
        }
    }

    hide() {
        if (MapBox.map.getSource('tails') !== undefined) {
            MapBox.map.setLayoutProperty('tails', 'visibility', 'none')
        }

        if (MapBox.map.getSource('tails-waypoints') !== undefined) {
            MapBox.map.setLayoutProperty('tails-waypoints', 'visibility', 'none')
        }
    }

    addEventsListeners() {
        MapBox.map.on('mouseenter', 'tails', (e) => {
            const tailData = e.features[0].properties

            MyFleetPopupComponent.$data.display = 'block'
            MyFleetPopupComponent.$data.identifier = tailData.identifier
            MyFleetPopupComponent.$data.aircraftName = tailData.aircraftName
            MyFleetPopupComponent.$data.origin = tailData.origin
            MyFleetPopupComponent.$data.destination = tailData.destination
            MyFleetPopupComponent.$data.originIATA = tailData.originIATA
            MyFleetPopupComponent.$data.destinationIATA = tailData.destinationIATA
            MyFleetPopupComponent.$data.landingTime = tailData.landingTime
            MyFleetPopupComponent.$data.scheduledTime = tailData.scheduledTime
            MyFleetPopupComponent.$data.groundSpeedKilometers = tailData.groundSpeedKilometers
            MyFleetPopupComponent.$data.groundSpeedMiles = tailData.groundSpeedMiles
            MyFleetPopupComponent.$data.altitudeMeters = tailData.altitudeMeters
            MyFleetPopupComponent.$data.altitudeFeet = tailData.altitudeFeet
            MyFleetPopupComponent.$data.directDistanceInKilometers = tailData.directDistanceInKilometers
            MyFleetPopupComponent.$data.directDistanceInMiles = tailData.directDistanceInMiles

            MapBox.map.getCanvas().style.cursor = 'pointer'
        })

        MapBox.map.on('mousemove', 'tails', (e) => {
            MyFleetPopupComponent.$data.leftPosition = e.point.x + 15 + 'px'
            MyFleetPopupComponent.$data.topPosition = e.point.y + 15 + 'px'
        })

        MapBox.map.on('mouseleave', 'tails', (e) => {
            MyFleetPopupComponent.$data.display = 'none'
            MapBox.map.getCanvas().style.cursor = 'inherit'
        })
    }
}

const Tails = new TailsModule()
export default Tails
